import { Cpu, RotateCcw, Sparkles } from "lucide-react";

import { strings } from "@perceptron/constants";
import { Button } from "@perceptron/ui/components/button";

import { useTrackingInspectorStore } from "./controller";
import { RecoverySummary } from "./RecoverySummary";

const copy = strings.trackingInspector;

export function RunPanel({ onInspectProblems }: { onInspectProblems: () => void }) {
  const summary = useTrackingInspectorStore((state) => state.summary);
  const rerun = useTrackingInspectorStore((state) => state.rerun);
  const setTab = useTrackingInspectorStore((state) => state.setTab);
  const hasProblems = useTrackingInspectorStore((state) =>
    state.snapshot?.rows.some((row) => row.status === "error" || row.status === "rejected" || row.warnings.length > 0),
  );
  const recovery = summary?.recovery;
  const finished = summary ? summary.counts.ready + summary.counts.failed : 0;
  const total = Math.max(1, summary?.counts.total ?? 0);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-foreground-secondary">{copy.runHint}</p>
        <Button
          aria-label={copy.rerun}
          className="h-8 shrink-0 gap-1.5 text-xs shadow-none"
          size="sm"
          title={copy.rerunHint}
          type="button"
          variant="outline"
          onClick={rerun}
        >
          <RotateCcw aria-hidden="true" className="size-3.5" />
          {copy.rerunShort}
        </Button>
      </div>
      <section aria-label={copy.localTracking} className="space-y-4">
        <h4 className="flex items-center gap-2 font-medium">
          <Cpu aria-hidden="true" className="size-4 text-foreground-secondary" />
          {copy.localTracking}
        </h4>
        {summary ? (
          <>
            <div className="flex items-end justify-between gap-4">
              <div>
                <p className="text-xs text-foreground-secondary">{copy.intervalsFinished}</p>
                <p className="mt-1 text-2xl font-medium tabular-nums">
                  {finished}
                  <span className="ml-1 text-base font-normal text-foreground-secondary">/ {summary.counts.total}</span>
                </p>
              </div>
            </div>
            <div
              aria-label={copy.intervalsFinished}
              aria-valuemax={total}
              aria-valuemin={0}
              aria-valuenow={finished}
              className="flex h-2 overflow-hidden rounded-full bg-background-alt-2"
              role="progressbar"
            >
              <div
                className="h-full bg-foreground-secondary transition-[width] duration-200 motion-reduce:transition-none"
                style={{ width: (summary.counts.ready / total) * 100 + "%" }}
              />
              <div
                className="h-full bg-error transition-[width] duration-200 motion-reduce:transition-none"
                style={{ width: (summary.counts.failed / total) * 100 + "%" }}
              />
            </div>
            <dl className="grid grid-cols-2 gap-3 [@container(min-width:480px)]:grid-cols-4">
              {[
                [copy.processed, summary.counts.ready],
                [copy.workers, summary.counts.processing + "/" + (summary.workerCount ?? 0)],
                [copy.pending, summary.counts.pending],
                [copy.failed, summary.counts.failed],
              ].map(([label, value]) => (
                <div key={label}>
                  <dt className="text-xs text-foreground-secondary">{label}</dt>
                  <dd className="mt-1 font-medium tabular-nums">{value}</dd>
                </div>
              ))}
            </dl>
            <p className="text-xs text-foreground-secondary">{copy.localRunHint}</p>
          </>
        ) : (
          <p className="text-sm text-foreground-secondary">{copy.waitingForData}</p>
        )}
        {summary?.workerError && (
          <p className="rounded-md bg-background-error p-3 text-xs" role="alert">
            {copy.workerError}: {summary.workerError}
          </p>
        )}
        {hasProblems && (
          <Button
            className="h-8 text-xs shadow-none"
            size="sm"
            title={copy.inspectProblemsHint}
            type="button"
            variant="outline"
            onClick={onInspectProblems}
          >
            {copy.inspectProblems}
          </Button>
        )}
      </section>
      <div className="border-t border-border pt-5">
        {recovery?.enabled && recovery.available ? (
          <RecoverySummary recovery={recovery} />
        ) : (
          <section aria-label={copy.recovery.title} className="space-y-3 rounded-lg border border-border p-3">
            <h4 className="flex items-center gap-2 font-medium">
              <Sparkles aria-hidden="true" className="size-4 text-foreground-secondary" />
              {copy.recoveryOff}
            </h4>
            <p className="text-xs text-foreground-secondary">
              {recovery?.available ? copy.recoveryOffHint : copy.recovery.unavailable}
            </p>
            <Button
              className="h-8 text-xs shadow-none"
              size="sm"
              type="button"
              variant="outline"
              onClick={() => setTab("strategies")}
            >
              {copy.configureRecovery}
            </Button>
          </section>
        )}
      </div>
    </div>
  );
}
